<template>
  <div>
    <nav-menu />

    <!-- Header -->
    <div class="w-full bg-white border-b border-gray-200">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <h1 class="text-lg font-semibold text-gray-900">Test Reports</h1>
          </div>
          <div class="flex items-center space-x-4 text-sm">
            <div class="flex items-center space-x-1.5">
              <span class="w-2 h-2 rounded-full bg-amber-400"></span>
              <span class="text-gray-600"><span class="font-semibold text-gray-900">{{ pendingFiles.length }}</span> pending</span>
            </div>
            <div class="flex items-center space-x-1.5">
              <span class="w-2 h-2 rounded-full bg-green-500"></span>
              <span class="text-gray-600"><span class="font-semibold text-gray-900">{{ matchedFiles.length }}</span> matched</span>
            </div>
          </div>
        </div>
        <!-- Tabs -->
        <div class="flex items-center mt-3 -mb-4 border-b border-gray-200 -mx-6 px-6">
          <button @click="activeTab = 'reconcile'" :class="activeTab === 'reconcile' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'" class="mr-6 pb-3 border-b-2 text-sm font-medium transition-colors">
            Reconcile
            <span v-if="pendingFiles.length" class="ml-1.5 bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ pendingFiles.length }}</span>
          </button>
          <button @click="activeTab = 'matched'" :class="activeTab === 'matched' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'" class="pb-3 border-b-2 text-sm font-medium transition-colors">
            Matched
            <span class="ml-1.5 bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">{{ matchedFiles.length }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== RECONCILE TAB ==================== -->
    <div v-if="activeTab === 'reconcile'" class="bg-gray-50 min-h-screen">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 py-6">

        <!-- Column headers -->
        <div class="flex mb-4 px-1">
          <div class="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Test Report</div>
          <div class="w-14 flex-shrink-0"></div>
          <div class="flex-1 text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Job Match</div>
        </div>

        <!-- Empty state -->
        <div v-if="pendingFiles.length === 0" class="bg-white rounded-2xl border border-dashed border-gray-300 p-16 text-center">
          <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <i class="fa-solid fa-check text-2xl text-green-600"></i>
          </div>
          <p class="text-gray-900 font-semibold mb-1">All caught up</p>
          <p class="text-sm text-gray-400">No test reports waiting to be matched</p>
        </div>

        <!-- Reconciliation rows -->
        <transition-group name="reconcile-row" tag="div" class="space-y-3">
          <div
            v-for="file in pendingFiles"
            :key="file.id"
            class="reconcile-row-item"
          >
            <!-- Matched overlay animation -->
            <transition name="match-flash">
              <div v-if="file._matched" class="absolute inset-0 z-10 bg-green-50 border-2 border-green-400 rounded-2xl flex items-center justify-center">
                <div class="text-center">
                  <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-2 match-check">
                    <i class="fa-solid fa-check text-xl text-white"></i>
                  </div>
                  <p class="text-sm font-semibold text-green-700">Matched</p>
                </div>
              </div>
            </transition>

            <div class="flex items-stretch bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

              <!-- LEFT: Test Report Card -->
              <div class="flex-1 min-w-0 p-5 border-r border-gray-100">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-file-pdf text-red-500 text-sm"></i>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold text-gray-900 truncate">{{ file.subject || 'Test Report' }}</p>
                    <p class="text-xs text-gray-400">{{ formatDate(file.created) }}</p>
                  </div>
                  <button
                    @click="deleteFile(file)"
                    class="flex-shrink-0 text-gray-300 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                    title="Delete file"
                  >
                    <i class="fa-solid fa-trash text-xs"></i>
                  </button>
                </div>

                <div v-if="file.from" class="flex items-center text-xs text-gray-500 mb-3">
                  <i class="fa-solid fa-envelope text-gray-300 mr-2 w-3 text-center"></i>
                  <span class="truncate">{{ file.from }}</span>
                </div>

                <div v-if="file.attachments && file.attachments.length" class="space-y-1.5">
                  <a
                    v-for="(att, idx) in file.attachments"
                    :key="idx"
                    :href="att.downloadUrl"
                    target="_blank"
                    class="flex items-center text-xs text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors group"
                  >
                    <i class="fa-solid fa-file-pdf text-red-400 mr-2"></i>
                    <span class="truncate flex-1">{{ att.filename }}</span>
                    <i class="fa-solid fa-arrow-up-right-from-square text-gray-300 group-hover:text-gray-500 ml-2 text-[10px]"></i>
                  </a>
                </div>
                <div v-else class="text-xs text-gray-400 italic">No attachments</div>

                <!-- Extracted job number indicator -->
                <div v-if="file.extractedJobNumber" class="mt-3 flex items-center text-xs">
                  <span class="inline-flex items-center bg-indigo-50 text-indigo-600 font-medium px-2 py-1 rounded-md">
                    <i class="fa-solid fa-robot mr-1.5 text-indigo-400"></i>
                    Found #{{ file.extractedJobNumber }}
                  </span>
                  <span v-if="file.matchConfidence" :class="confidenceBadgeClass(file.matchConfidence)" class="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase">
                    {{ file.matchConfidence }}
                  </span>
                </div>
              </div>

              <!-- CENTER: Match Button -->
              <div class="flex flex-col items-center justify-center w-14 flex-shrink-0 bg-gray-50">
                <button
                  v-if="file._selectedJob || getSuggestedJob(file)"
                  @click="confirmMatch(file, file._selectedJob || getSuggestedJob(file))"
                  :disabled="file._matching"
                  class="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 active:scale-95 text-white shadow-lg shadow-green-200 transition-all flex items-center justify-center disabled:opacity-50"
                  title="Match"
                >
                  <i v-if="file._matching" class="fa-solid fa-spinner fa-spin text-sm"></i>
                  <i v-else class="fa-solid fa-check text-sm"></i>
                </button>
                <div v-else class="w-10 h-10 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center">
                  <i class="fa-solid fa-link-slash text-xs"></i>
                </div>
              </div>

              <!-- RIGHT: Job Card -->
              <div class="flex-1 min-w-0 p-5 border-l border-gray-100">

                <!-- Suggested job (AI match found) -->
                <template v-if="!file._overriding && (file._selectedJob || getSuggestedJob(file))">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <div class="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <i class="fa-solid fa-briefcase text-indigo-500 text-sm"></i>
                      </div>
                      <div>
                        <p class="text-sm font-semibold text-gray-900">
                          <span class="text-indigo-600">#{{ (file._selectedJob || getSuggestedJob(file)).jobNumber }}</span>
                        </p>
                        <p class="text-xs text-gray-400">Job</p>
                      </div>
                    </div>
                    <button @click="$set(file, '_overriding', true); $set(file, '_searchTerm', ''); $set(file, '_searchResults', [])" class="text-xs text-gray-400 hover:text-indigo-600 transition-colors">
                      Change
                    </button>
                  </div>

                  <p class="text-sm font-medium text-gray-800 mb-1">{{ (file._selectedJob || getSuggestedJob(file)).projectName }}</p>
                  <p class="text-xs text-gray-500 mb-1">{{ (file._selectedJob || getSuggestedJob(file)).company }}</p>
                  <p v-if="(file._selectedJob || getSuggestedJob(file)).formattedAddress" class="text-xs text-gray-400">
                    <i class="fa-solid fa-location-dot mr-1"></i>{{ (file._selectedJob || getSuggestedJob(file)).formattedAddress }}
                  </p>

                </template>

                <!-- No match / search mode -->
                <template v-else>
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-xs font-medium text-gray-500 uppercase">Find a Job</span>
                    <button v-if="file._overriding && getSuggestedJob(file)" @click="$set(file, '_overriding', false); $set(file, '_selectedJob', null)" class="text-xs text-gray-400 hover:text-gray-600">
                      Back to suggestion
                    </button>
                  </div>

                  <div class="relative mb-2">
                    <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                    <input
                      v-model="file._searchTerm"
                      @input="searchJobsForFile(file)"
                      type="text"
                      placeholder="Search by job number or name..."
                      class="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-3 h-10 outline-none focus:border-indigo-400 focus:bg-white transition-colors"
                    />
                  </div>

                  <!-- Search results -->
                  <div v-if="file._searchResults && file._searchResults.length" class="max-h-48 overflow-y-auto rounded-xl border border-gray-100 bg-white divide-y divide-gray-50">
                    <div
                      v-for="job in file._searchResults"
                      :key="job.id"
                      @click="selectJobForFile(file, job)"
                      class="px-3 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors"
                    >
                      <div class="flex items-center justify-between">
                        <div>
                          <span class="text-sm font-semibold text-indigo-600">#{{ job.jobNumber }}</span>
                          <span class="text-sm text-gray-700 ml-1.5">{{ job.projectName }}</span>
                        </div>
                        <i class="fa-solid fa-chevron-right text-gray-300 text-xs"></i>
                      </div>
                      <p class="text-xs text-gray-400 mt-0.5">{{ job.company || '' }}</p>
                    </div>
                  </div>

                  <div v-else-if="file._searchTerm && file._searchTerm.length >= 1 && file._searchResults && file._searchResults.length === 0" class="text-center py-6">
                    <i class="fa-solid fa-magnifying-glass text-gray-200 text-2xl mb-2"></i>
                    <p class="text-xs text-gray-400">No jobs match that search</p>
                  </div>

                  <div v-else-if="!file._searchTerm" class="text-center py-6">
                    <i class="fa-solid fa-briefcase text-gray-200 text-2xl mb-2"></i>
                    <p class="text-xs text-gray-400">Type a job number or project name</p>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>

    <!-- ==================== MATCHED TAB ==================== -->
    <div v-if="activeTab === 'matched'" class="bg-gray-50 min-h-screen">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 py-6">

        <div v-if="matchedFiles.length === 0" class="bg-white rounded-2xl border border-dashed border-gray-300 p-16 text-center">
          <i class="fa-solid fa-inbox text-3xl text-gray-200 mb-3"></i>
          <p class="text-sm text-gray-400">No matched test reports yet</p>
        </div>

        <div class="space-y-2">
          <div
            v-for="file in matchedFiles"
            :key="file.id"
            class="bg-white rounded-xl border border-gray-200 px-5 py-3 flex items-center gap-4 hover:border-gray-300 transition-colors"
          >
            <!-- PDF icon -->
            <div class="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
              <i class="fa-solid fa-file-pdf text-red-400 text-sm"></i>
            </div>

            <!-- File info -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">{{ file.subject }}</p>
              <p class="text-xs text-gray-400 truncate">{{ file.from }} &middot; {{ formatDate(file.created) }}</p>
            </div>

            <!-- Linked job -->
            <div class="hidden sm:flex items-center gap-3 flex-shrink-0">
              <i class="fa-solid fa-link text-green-400 text-xs"></i>
              <div class="text-right">
                <p class="text-sm font-semibold text-indigo-600">#{{ file.jobNumber || '—' }}</p>
                <p class="text-xs text-gray-400">{{ file.jobProjectName || 'Job' }}</p>
              </div>
            </div>

            <!-- Attachments -->
            <div class="hidden md:flex items-center gap-1 flex-shrink-0">
              <a
                v-for="(att, idx) in (file.attachments || []).slice(0, 2)"
                :key="idx"
                :href="att.downloadUrl"
                target="_blank"
                class="text-xs text-gray-400 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 px-2 py-1 rounded transition-colors"
                :title="att.filename"
              >
                <i class="fa-solid fa-download"></i>
              </a>
            </div>

            <!-- Unlink -->
            <button
              @click="unlinkFile(file)"
              class="text-xs text-gray-300 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
              title="Unlink"
            >
              <i class="fa-solid fa-link-slash"></i>
            </button>

            <!-- Delete -->
            <button
              @click="deleteFile(file)"
              class="text-xs text-gray-300 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
              title="Delete file"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavMenu from "@/components/headers/navMenu";
import { collection, query, orderBy, onSnapshot, getDocs, where, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";

const API_BASE = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets';

export default {
  data() {
    return {
      activeTab: 'reconcile',
      files: [],
      unsubFiles: null,
      jobsCache: [],
      jobsCacheLoaded: false,
      searchTimer: null,
    };
  },
  computed: {
    pendingFiles() {
      return this.files.filter(f => f.status === 'unallocated' && !f._dismissed);
    },
    matchedFiles() {
      return this.files.filter(f => f.status === 'allocated');
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    confidenceBadgeClass(confidence) {
      if (confidence === 'high') return 'bg-green-100 text-green-700';
      if (confidence === 'medium') return 'bg-yellow-100 text-yellow-700';
      return 'bg-gray-100 text-gray-500';
    },
    async loadJobsCache() {
      if (this.jobsCacheLoaded) return;
      try {
        const snap = await getDocs(
          query(collection(db, 'jobs'), where('active', '==', true))
        );
        this.jobsCache = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        this.jobsCacheLoaded = true;
        this.autoMatchFiles();
      } catch (e) {
        console.error('Failed to load jobs cache:', e);
      }
    },
    autoMatchFiles() {
      if (!this.jobsCacheLoaded) return;
      this.files.forEach(file => {
        if (file.status !== 'unallocated' || file._selectedJob) return;
        if (!file.extractedJobNumber) return;
        const match = this.jobsCache.find(j => j.jobNumber === file.extractedJobNumber);
        if (match) {
          this.$set(file, '_selectedJob', match);
        }
      });
    },
    getSuggestedJob(file) {
      if (!file.extractedJobNumber) return null;
      return this.jobsCache.find(j => j.jobNumber === file.extractedJobNumber) || null;
    },
    selectJobForFile(file, job) {
      this.$set(file, '_selectedJob', job);
      this.$set(file, '_overriding', false);
      this.$set(file, '_searchTerm', '');
      this.$set(file, '_searchResults', []);
    },
    searchJobsForFile(file) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const term = (file._searchTerm || '').trim().replace(/^#/, '');
        if (term.length < 1) {
          this.$set(file, '_searchResults', []);
          return;
        }
        const results = this.jobsCache
          .filter(j => {
            if (j.jobNumber && j.jobNumber.includes(term)) return true;
            if (j.projectName && j.projectName.toLowerCase().includes(term.toLowerCase())) return true;
            return false;
          })
          .slice(0, 20);
        this.$set(file, '_searchResults', results);
      }, 200);
    },
    async getToken() {
      const user = getAuth().currentUser;
      if (!user) return null;
      return user.getIdToken();
    },
    async confirmMatch(file, job) {
      this.$set(file, '_matching', true);
      try {
        const token = await this.getToken();
        await fetch(`${API_BASE}/api/v1/files/${file.id}/allocate`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ jobId: job.id }),
        });
        // Show matched animation
        this.$set(file, '_matched', true);
        this.$set(file, '_matching', false);
        // After animation, dismiss the row (firestore snapshot will move it to allocated)
        setTimeout(() => {
          this.$set(file, '_dismissed', true);
        }, 1200);
      } catch (e) {
        console.error('Match error:', e);
        this.$set(file, '_matching', false);
      }
    },
    async deleteFile(file) {
      try {
        await deleteDoc(doc(db, 'incomingFiles', file.id));
      } catch (e) {
        console.error('Delete error:', e);
      }
    },
    async unlinkFile(file) {
      try {
        const token = await this.getToken();
        await fetch(`${API_BASE}/api/v1/files/${file.id}/unallocate`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (e) {
        console.error('Unlink error:', e);
      }
    },
  },
  async created() {
    const q = query(
      collection(db, 'incomingFiles'),
      orderBy('created', 'desc')
    );
    this.unsubFiles = onSnapshot(q, (snap) => {
      this.files = snap.docs.map(d => {
        const data = d.data();
        const existing = this.files.find(f => f.id === d.id);
        return {
          id: d.id,
          ...data,
          created: data.created?.toDate?.()?.toISOString() || null,
          _overriding: existing ? existing._overriding : false,
          _searchTerm: existing ? existing._searchTerm : '',
          _searchResults: existing ? existing._searchResults : [],
          _selectedJob: existing ? existing._selectedJob : null,
          _matched: existing ? existing._matched : false,
          _dismissed: existing ? existing._dismissed : false,
          _matching: existing ? existing._matching : false,
        };
      });
      this.$nextTick(() => this.autoMatchFiles());
    }, (err) => {
      console.error('Files snapshot error:', err);
    });
    await this.loadJobsCache();
  },
  beforeDestroy() {
    if (this.unsubFiles) this.unsubFiles();
  },
  components: {
    NavMenu,
  },
};
</script>

<style scoped>
/* Row slide-out transitions */
.reconcile-row-item {
  position: relative;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.reconcile-row-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.reconcile-row-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  transform: translateX(40px);
}

.reconcile-row-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.reconcile-row-enter {
  opacity: 0;
  transform: translateY(-10px);
}

/* Match flash overlay */
.match-flash-enter-active {
  transition: all 0.3s ease-out;
}
.match-flash-enter {
  opacity: 0;
  transform: scale(0.95);
}
.match-flash-leave-active {
  transition: all 0.4s ease-in;
}
.match-flash-leave-to {
  opacity: 0;
}

/* Checkmark pop-in */
@keyframes check-pop {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}
.match-check {
  animation: check-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
</style>
